import React from "react";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

/**
 * The segment type dropdown. Shared between AddSegmentDialog and
 * EditSegmentDialog so the canonical list of segment types lives in
 * exactly one place.
 *
 * NOTE: changing a segment's type also clears `vendor_name` (different
 * type → different vendor pool) and resets `cruise_type` unless the
 * new type is still 'cruise'. Those side-effects are encoded inside the
 * onChange below so callers can't forget them.
 *
 * Props:
 *   value          — current `formData.type` string.
 *   currentVendor  — current `formData.vendor_name`. We need it so we
 *                    can clear the matching `selectedVendor` local
 *                    state in the caller via `onVendorReset`.
 *   currentCruiseType — current `formData.cruise_type`. Preserved when
 *                       the new type is still 'cruise', cleared otherwise.
 *   onChange       — patch callback. Receives at minimum `{ type }` and
 *                    may also include `{ vendor_name: "", cruise_type: ...}`.
 *   onTypeChanged  — optional hook fired AFTER onChange so the caller
 *                    can clear local UI state like `selectedVendor`.
 *                    Avoids the need for refs/effects in the caller.
 *   testIdPrefix   — 'segment' (default) or 'edit-segment'. Drives the
 *                    SelectTrigger's data-testid.
 */
export const SEGMENT_TYPE_OPTIONS = [
  { value: "flight", label: "Flight" },
  { value: "train", label: "Train" },
  { value: "bus", label: "Bus" },
  { value: "cruise", label: "Cruise" },
  { value: "car_rental", label: "Car Rental" },
  { value: "rideshare", label: "Rideshare" },
  { value: "accommodation", label: "Accommodation" },
  { value: "activity", label: "Activity / Event" },
];

export const SegmentTypeSelect = ({
  value,
  currentCruiseType = null,
  onChange,
  onTypeChanged,
  testIdPrefix = "segment",
}) => {
  const handleChange = (newType) => {
    const patch = {
      type: newType,
      vendor_name: "", // different type → different vendor pool
      cruise_type: newType === "cruise" ? currentCruiseType : null,
    };
    onChange(patch);
    if (onTypeChanged) onTypeChanged(newType);
  };

  return (
    <div className="grid gap-2">
      <Label htmlFor={`${testIdPrefix}-type-select`}>Type *</Label>
      <Select value={value} onValueChange={handleChange}>
        <SelectTrigger
          id={`${testIdPrefix}-type-select`}
          data-testid={`${testIdPrefix}-type-select`}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent position="popper" className="z-[100]">
          {SEGMENT_TYPE_OPTIONS.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default SegmentTypeSelect;
